import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PublishIcon from "@mui/icons-material/Publish";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { capture } from "../analytics/posthog";
import ContentScreenshotThumbnail from "../components/ContentScreenshotThumbnail";
import LessonPlanRenderer, {
  LessonPlanSelectSession,
} from "../components/lesson-plan/LessonPlanRenderer";
import { areAllSlidesTrackedForSession } from "../components/lesson-plan/lessonPlanProgressUtils";
import ApiClient from "../config/ApiClient";
import { LessonPlanEditorProvider, useLessonPlanEditor } from "../context/LessonPlanEditorContext";
import { LessonPlanRecordingProvider, useLessonPlanRecording } from "../context/LessonPlanRecordingContext";
import { useCheckoutTrialUpgradedNotice } from "../hooks/useCheckoutTrialUpgradedNotice";
import { LessonPlanModel } from "../object-actions/types/types";

const LessonPlanViewContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const posthog = usePostHog();
  const { isEditMode, loading: saveLoading, hasChanges, saveAll, resetChanges, toggleEditMode, lessonPlan, setLessonPlan, getSessionWithEdits } = useLessonPlanEditor();

  const refetchLessonPlan = useCallback(async () => {
    if (!lessonPlan?.id) {
      return;
    }
    const response = await ApiClient.get<LessonPlanModel>(
      `/api/lesson-plan/${lessonPlan.id}?include_sessions=true`,
    );
    if (response.success && response.data) {
      setLessonPlan(response.data);
    }
  }, [lessonPlan?.id, setLessonPlan]);

  useCheckoutTrialUpgradedNotice(refetchLessonPlan);
  const { activeSessionId } = useLessonPlanRecording();
  const [isPublishing, setIsPublishing] = useState(false);
  const [sessionPickerAnchor, setSessionPickerAnchor] = useState<HTMLElement | null>(null);
  const selectSessionRef = useRef<LessonPlanSelectSession | null>(null);
  const { id } = useParams();

  const isCoach = lessonPlan?.my_roles?.includes('coach') ?? false;
  const isPublished = !!lessonPlan?.coach_delivered;
  const [isStartingAi, setIsStartingAi] = useState(false);
  const canStartWithAi =
    !lessonPlan?.has_human_coach &&
    !!lessonPlan?.assignment?.id;
  const showScheduleCta =
    lessonPlan?.coaching_mode !== 'ai' &&
    (lessonPlan?.has_human_coach ?? false) &&
    !!lessonPlan?.id &&
    isPublished;

  const sessions = lessonPlan?.sessions || [];
  const sessionPickerOpen = Boolean(sessionPickerAnchor);

  const activeSession = useMemo(() => {
    return sessions.find(session => session.id === activeSessionId) ?? sessions[0] ?? null;
  }, [sessions, activeSessionId]);

  const pageTitle = useMemo(() => {
    if (!activeSession) return 'Lesson Plan';
    const session = getSessionWithEdits(activeSession);
    return session.topic || session.theme || `Session ${session.session_number ?? session.id ?? ''}`;
  }, [activeSession, getSessionWithEdits]);

  const subtitle = useMemo(() => {
    if (!activeSession) return null;
    const session = getSessionWithEdits(activeSession);
    return session.objective || null;
  }, [activeSession, getSessionWithEdits]);

  const closeSessionPicker = () => setSessionPickerAnchor(null);

  const handleSelectSessionReady = useCallback((selectSession: LessonPlanSelectSession) => {
    selectSessionRef.current = selectSession;
  }, []);

  const handlePickSession = (sessionId: number) => {
    selectSessionRef.current?.(sessionId, 'dropdown');
    closeSessionPicker();
  };

  const handleStartWithAi = async () => {
    const assignmentId = lessonPlan?.assignment?.id;
    if (!assignmentId || isStartingAi || !id) {
      return;
    }
    capture(posthog, "start_program_with_ai_clicked", {
      assignment_id: assignmentId,
      source: "lesson_plan",
    });
    setIsStartingAi(true);
    try {
      const response = await ApiClient.post(
        `/api/payment-assignments/${assignmentId}/start-with-ai/`,
        {},
      );
      if (response.success) {
        const refetchResponse = await ApiClient.get<LessonPlanModel>(
          `/api/lesson-plan/${id}?include_sessions=true`,
        );
        if (refetchResponse.success && refetchResponse.data) {
          setLessonPlan(refetchResponse.data);
        }
        enqueueSnackbar('Your AI coach is now guiding this program.', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to start with an AI coach', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error starting with AI coach:', error);
      enqueueSnackbar('Error starting with an AI coach', { variant: 'error' });
    } finally {
      setIsStartingAi(false);
    }
  };

  const handleSave = async () => {
    capture(posthog, "lesson_plan_save_clicked", { lesson_plan_id: lessonPlan?.id });
    await saveAll();
    // Optionally remove hash after save
    if (window.location.hash === '#editing=true') {
      window.location.hash = '';
    }
  };

  const handleCancel = () => {
    capture(posthog, "lesson_plan_cancel_clicked", { lesson_plan_id: lessonPlan?.id });
    resetChanges();
    window.location.hash = '';
  };

  const handlePublish = async () => {
    if (!lessonPlan?.id || isPublishing || !id) {
      return;
    }
    capture(posthog, "lesson_plan_publish_clicked", { lesson_plan_id: lessonPlan.id });
    setIsPublishing(true);
    try {
      const response = await ApiClient.post(`/api/lesson-plan/${lessonPlan.id}/publish/`, {});

      if (response.success) {
        capture(posthog, "lesson_plan_published", { lesson_plan_id: lessonPlan.id });
        // Refetch the lesson plan to get updated data with sessions
        const refetchResponse = await ApiClient.get<LessonPlanModel>(
          `/api/lesson-plan/${id}?include_sessions=true`
        );

        if (refetchResponse.success && refetchResponse.data) {
          setLessonPlan(refetchResponse.data);
          enqueueSnackbar('Lesson plan published successfully!', { variant: 'success' });
        } else {
          enqueueSnackbar('Lesson plan published, but failed to refresh data', { variant: 'warning' });
        }
      } else {
        enqueueSnackbar('Failed to publish lesson plan', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error publishing lesson plan:', error);
      enqueueSnackbar('Error publishing lesson plan', { variant: 'error' });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!lessonPlan) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ mb: 2 }}>
        {canStartWithAi && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              color="secondary"
              startIcon={<SmartToyOutlined />}
              onClick={handleStartWithAi}
              disabled={isStartingAi}
              data-attr="lesson-plan-view_start-with-ai"
            >
              {isStartingAi ? 'Starting…' : 'Start with AI'}
            </Button>
          </Box>
        )}
        {/* Back to progress report (coach and parent when athlete context exists) */}

        {/* Header: current session title is the page H1; screenshot + edit for coach/parent */}
        <Box sx={{ mb: 3, display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap-reverse', gap: 2 }}>

          <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, alignItems: 'flex-start' }}>
            <ContentScreenshotThumbnail
              screenshotLight={lessonPlan.screenshot_light}
              screenshotDark={lessonPlan.screenshot_dark}
              alt="Lesson plan preview"
              data-attr="lesson-plan-view_screenshot-thumbnail"
            />
            <Box >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', letterSpacing: 1 }}
              >
                Lesson Plan
              </Typography>

              <Typography
                variant="h3"
                component="h1"
                data-testid="lesson-plan-session-title"
                data-attr="lesson-plan-session-title"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  marginLeft: '-3px'
                }}
              >
                {pageTitle}
              </Typography>
              {subtitle && (
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {showScheduleCta && (
              <Button
                variant="outlined"
                component={Link}
                to={`/lesson-plan/${lessonPlan.id}/schedule`}
                startIcon={<EventAvailableIcon />}
                data-attr="lesson-plan-view_schedule-session"
              >
                Schedule session
              </Button>
            )}
            {/* Action Buttons (coach only) */}
            {isCoach && (

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

                <Button variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  component={Link} to={`/program-progress/${lessonPlan?.assignment?.id}`}>
                  Review {lessonPlan?.athlete?.str}'s Progress Report
                </Button>

                {!isEditMode ? (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => {
                        capture(posthog, "lesson_plan_edit_toggle_clicked", { lesson_plan_id: lessonPlan?.id });
                        toggleEditMode();
                      }}
                      data-attr="lesson-plan-view_edit"
                    >
                      Edit
                    </Button>
                    {!isPublished && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handlePublish}
                        disabled={isPublishing}
                        startIcon={isPublishing ? <CircularProgress size={20} /> : <PublishIcon />}
                        data-attr="lesson-plan-view_publish"
                      >
                        {isPublishing ? 'Publishing...' : 'Publish'}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      disabled={saveLoading || !hasChanges}
                      startIcon={saveLoading ? <CircularProgress size={20} /> : undefined}
                      data-attr="lesson-plan-view_save"
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saveLoading}
                      data-attr="lesson-plan-view_cancel"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Typography variant="overline"
              color="text.secondary"
              sx={{ display: 'block' }}>
              <Button
                onClick={event => setSessionPickerAnchor(event.currentTarget)}
                endIcon={<ArrowDropDownIcon />}
                variant="outlined"
                size="small"
                aria-label="Select another session"
                aria-haspopup="dialog"
                aria-expanded={sessionPickerOpen}
                data-attr="lesson-plan-renderer_session-select"
                data-testid="lesson-plan-session-select"
              >
                View Session List
              </Button>
              <Popover
                open={sessionPickerOpen}
                anchorEl={sessionPickerAnchor}
                onClose={closeSessionPicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: { minWidth: 280, maxWidth: 420, maxHeight: 360 },
                  },
                }}
              >
                <List
                  dense
                  disablePadding
                  aria-label="Sessions"
                  data-testid="lesson-plan-session-select-popover"
                  data-attr="lesson-plan-session-select-popover"
                >
                  {sessions.map((session, index) => {
                    const label = session.topic || session.theme || `Session ${session.session_number ?? index + 1}`;
                    const isComplete = Boolean(session.completed_at);
                    const isCurrent = session.id === activeSessionId;
                    const showCompleteIcon = isComplete || areAllSlidesTrackedForSession(session);
                    return (
                      <ListItemButton
                        key={session.id}
                        selected={isCurrent}
                        onClick={() => handlePickSession(session.id)}
                        data-testid={`lesson-plan-session-select-option--${index + 1}`}
                      >
                        {showCompleteIcon && (
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <TaskAltIcon
                              color="success"
                              fontSize="small"
                              titleAccess={isComplete ? 'Session completed' : 'All slides tracked'}
                            />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ noWrap: true }}
                          inset={!showCompleteIcon}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Popover>
            </Typography>
          </Box>

        </Box>

        <Box>
          <LessonPlanRenderer
            entity={lessonPlan as any}
            onSelectSessionReady={handleSelectSessionReady}
          />
        </Box>

      </Box>
    </Container>
  );
};

const LessonPlanView = () => {
  const { id } = useParams();
  const [initialLessonPlan, setInitialLessonPlan] = useState<LessonPlanModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {

    const fetchLessonPlan = async () => {
      if (!id) {
        setError("Lesson plan ID is required");
        setLoading(false);
        return;
      }

      try {
        // Request with include_sessions to get all nested data
        const response = await ApiClient.get<LessonPlanModel>(
          `/api/lesson-plan/${id}?include_sessions=true`
        );

        if (response.error) {
          setError(response.error);
        } else if (response.success && response.data) {
          setInitialLessonPlan(response.data);
        } else {
          setError("Failed to load lesson plan");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchLessonPlan();
  }, [id]);

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div id="LessonPlanView" data-testid="lesson-plan-view">
      <LessonPlanEditorProvider initialLessonPlan={initialLessonPlan}>
        <LessonPlanRecordingProvider>
          <LessonPlanViewContent />
        </LessonPlanRecordingProvider>
      </LessonPlanEditorProvider>
    </div>
  );
};

export default LessonPlanView;
